import { Upload, X } from 'lucide-react'
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import { theme } from '../../theme/theme'
import { uploadWorkoutFile } from '../../utils/uploadUtils'
import { Modal } from './Modal'

const ModalHeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.lg};
`

const ModalTitle = styled.h2`
  font-size: ${theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${theme.transitions.default};

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }
`

const ModalBodyContent = styled.div`
  padding: ${theme.spacing.lg};
`

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  &:last-child {
    margin-bottom: 0;
  }
`

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.md};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
`

const UploadArea = styled.div<{ $isDragOver?: boolean; $uploading?: boolean }>`
  border: 2px dashed ${({ theme, $isDragOver }) => ($isDragOver ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  background: ${({ theme, $isDragOver }) => ($isDragOver ? theme.colors.surfaceHover : theme.colors.cardBackground)};
  transition: ${({ theme }) => theme.transitions.default};
  cursor: ${(props) => (props.$uploading ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$uploading ? 0.7 : 1)};

  &:hover:not([data-uploading="true"]) {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary};
  }
`

const UploadIcon = styled.div<{ $uploading?: boolean }>`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.textSecondary};
`

const UploadText = styled.div`
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.md};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

const UploadSubtext = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.sm};
`

const HiddenFileInput = styled.input`
  display: none;
`

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};
  text-align: center;
`

const SuccessMessage = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};
  text-align: center;
`

interface UploadModalProps {
  isOpen: boolean
  onClose: () => void
  onUploadSuccess?: (workoutId: string, workoutTitle: string, workoutDate: string) => void
  onUploadError?: (error: string) => void
  athleteId: string
}

export const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  onClose,
  onUploadSuccess,
  onUploadError,
  athleteId,
}) => {
  const { user } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadMessage, setUploadMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null)

  // Load device connections when modal opens
  useEffect(() => {
    const loadDeviceConnections = async () => {
      if (!user || !isOpen) return

      try {
        const { data, error } = await supabase
          .from('device_connections')
          .select('device_type, is_active')
          .eq('user_id', user.id)
          .eq('is_active', true)

        if (error) {
          console.error('Error loading device connections:', error)
          return
        }

        const connections: Record<string, boolean> = {}
        data?.forEach((connection) => {
          connections[connection.device_type] = true
        })
      } catch (error) {
        console.error('Error loading device connections:', error)
      }
    }

    loadDeviceConnections()
  }, [user, isOpen])

  const handleFileUpload = useCallback(
    async (file: File) => {
      if (!file) return

      setIsUploading(true)
      setUploadMessage(null)

      try {
        const result = await uploadWorkoutFile(file, athleteId)
        setUploadMessage({ type: 'success', text: result.message })
        onUploadSuccess?.(result.workoutId, result.workoutTitle, result.workoutDate)

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed'
        setUploadMessage({ type: 'error', text: errorMessage })
        onUploadError?.(errorMessage)
      } finally {
        setIsUploading(false)
      }
    },
    [athleteId, onUploadSuccess, onUploadError]
  )

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) {
        handleFileUpload(file)
      }
    },
    [handleFileUpload]
  )

  const handleUploadAreaClick = useCallback(() => {
    if (!isUploading && fileInputRef.current) {
      fileInputRef.current.click()
    }
  }, [isUploading])

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      if (!isUploading) {
        setIsDragOver(true)
      }
    },
    [isUploading]
  )

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)

      if (isUploading) return

      const files = Array.from(e.dataTransfer.files)
      const file = files.find((f) => f.name.toLowerCase().endsWith('.fit'))

      if (file) {
        handleFileUpload(file)
      } else {
        setUploadMessage({ type: 'error', text: 'Please upload a .fit or .gpx file' })
      }
    },
    [isUploading, handleFileUpload]
  )

  const handleClose = useCallback(() => {
    if (!isUploading) {
      setUploadMessage(null)
      onClose()
    }
  }, [isUploading, onClose])

  const header = (
    <ModalHeaderContent>
      <ModalTitle>Upload Activity</ModalTitle>
      <CloseButton onClick={handleClose} disabled={isUploading}>
        <X size={20} />
      </CloseButton>
    </ModalHeaderContent>
  )

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="800px" header={header}>
      <ModalBodyContent>
        <Section>
          <SectionTitle>Upload File</SectionTitle>
          <UploadArea
            $isDragOver={isDragOver}
            $uploading={isUploading}
            onClick={handleUploadAreaClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            data-uploading={isUploading}
          >
            <UploadIcon $uploading={isUploading}>
              <Upload size={32} />
            </UploadIcon>
            <UploadText>
              {isUploading ? 'Uploading...' : isDragOver ? 'Drop your file here' : 'Click to upload or drag & drop'}
            </UploadText>
            <UploadSubtext>{!isUploading && 'Supports .fit files'}</UploadSubtext>
          </UploadArea>

          {uploadMessage &&
            (uploadMessage.type === 'error' ? (
              <ErrorMessage>{uploadMessage.text}</ErrorMessage>
            ) : (
              <SuccessMessage>{uploadMessage.text}</SuccessMessage>
            ))}
        </Section>

        <HiddenFileInput ref={fileInputRef} type="file" accept=".fit" onChange={handleFileInputChange} />
      </ModalBodyContent>
    </Modal>
  )
}
